import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { siteConfig } from '../siteData';
import { useFormsQuestions, useSubmitForm, useWebCourses } from '../hooks/sectionHooks';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

function CourseForm() {
  const { id: courseId, formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const courseName = location.state?.courseName || 'Сургалт';
  const { data: courses, isLoading: coursesLoading, error: coursesError } = useWebCourses();
  const courseAvailable = Array.isArray(courses) && courses.some(course => String(course.id) === courseId);
  const { data: formQuestions, isLoading, error } = useFormsQuestions(formId, courseAvailable);
  const submitForm = useSubmitForm();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!submitSuccess) {
      return undefined;
    }

    const redirectTimer = setTimeout(() => {
      navigate(`/course/${courseId}`);
    }, 5000);

    return () => clearTimeout(redirectTimer);
  }, [courseId, navigate, submitSuccess]);

  const handleInputChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courseAvailable || coursesError) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get questions to maintain order
      const questions = formQuestions.data || [];
      
      // Build answers object with question IDs as keys, maintaining order
      const answers = [];
      questions.forEach((question) => {
        const answer = formData[question.id];
        if (answer !== undefined && answer !== '') {
          answers[String(question.id)] = Array.isArray(answer) ? answer : String(answer);
        }
      });
      // remove empty entries
        const filteredAnswers = answers.filter(ans => ans !== undefined);
      
      // Extract user info from first three questions
      const questionIds = questions.map(q => q.id);
      
      const submissionData = {
        form_id: parseInt(formId),
        user_name: (formData[questionIds[0]] || "John") + ' ' + (formData[questionIds[1]] || "Doe"),
        user_email: formData[questionIds[3]] || "john.doe@example.com",
        user_phone: formData[questionIds[4]] || "+976 99999999",
        status : 'pending',
        answers: filteredAnswers
        
      };

      await submitForm(submissionData);
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError('Бүртгэл илгээхэд алдаа гарлаа. Дахин оролдоно уу.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderInput = (question) => {
    const commonProps = {
      id: `question-${question.id}`,
      value: formData[question.id] || '',
      onChange: (e) => handleInputChange(question.id, e.target.value),
      required: question.is_required,
      className: 'form-input'
    };

    const questionType = (question.question_type || question.input_type || '')
      .toLowerCase()
      .replace(/[\s_-]/g, '');
    const rawOptions = question.options;
    let options = [];

    if (Array.isArray(rawOptions)) {
      options = rawOptions;
    } else if (typeof rawOptions === 'string' && rawOptions.trim()) {
      try {
        const parsedOptions = JSON.parse(rawOptions);
        options = Array.isArray(parsedOptions) ? parsedOptions : [];
      } catch {
        options = rawOptions.split(/\r?\n|,/).map(option => option.trim()).filter(Boolean);
      }
    }

    switch (questionType) {
      case 'text':
        return <input type="text" {...commonProps} />;
      case 'email':
        return <input type="email" {...commonProps} />;
      case 'number':
        return <input type="number" {...commonProps} />;
      case 'phone':
        return <input type="tel" {...commonProps} />;
      case 'textarea':
        return <textarea {...commonProps} rows="4" />;
      case 'select':
      case 'selectdropdown':
        return (
          <select {...commonProps}>
            <option value="">Сонгох...</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'radio':
      case 'radiobuttons':
        return (
          <div className="radio-group">
            {options.map((option, idx) => (
              <label key={idx} className="radio-label">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={formData[question.id] === option}
                  onChange={(e) => handleInputChange(question.id, e.target.value)}
                  required={question.is_required}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
      case 'checkboxes':
        return (
          <div className="checkbox-group">
            {options.map((option, idx) => (
              <label key={idx} className="checkbox-label">
                <input
                  type="checkbox"
                  value={option}
                  checked={(formData[question.id] || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = Array.isArray(formData[question.id])
                      ? formData[question.id]
                      : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter(v => v !== option);
                    handleInputChange(question.id, newValues);
                  }}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'date':
        return <input type="date" {...commonProps} />;
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  if (coursesLoading || isLoading) {
    return (
      <div className="App">
        <header className="header">
          <div className="header-container">
            <div className="logo">
              <Link to="/">
                <img src={siteConfig.logo.src} alt={siteConfig.logo.alt} />
              </Link>
            </div>
          </div>
        </header>
        <main className="course-form-page">
          <div className="course-form-container">
            <LoadingSpinner />
          </div>
        </main>
      </div>
    );
  }

  if (coursesError || !courseAvailable || error || !formQuestions) {
    return (
      <div className="App">
        <header className="header">
          <div className="header-container">
            <div className="logo">
              <Link to="/">
                <img src={siteConfig.logo.src} alt={siteConfig.logo.alt} />
              </Link>
            </div>
          </div>
        </header>
        <main className="course-form-page">
          <div className="course-form-container">
            <h1>Алдаа гарлаа</h1>
            <p>{!coursesError && !courseAvailable ? 'Энэ сургалтын бүртгэл одоогоор нээлтгүй байна.' : 'Маягт олдсонгүй.'}</p>
            <Link to="/courses" className="back-link">← Сургалтууд</Link>
          </div>
        </main>
      </div>
    );
  }

  const questions = formQuestions.data || [];

  return (
    <div className="App">
      <Header />

      {/* Main Content */}
      <main className="course-form-page">
        <div className="course-form-container">
          <Link to={`/course/${courseId}`} className="back-link">← Буцах</Link>
          
          <div className="form-header">
            <h2 className="course-title">{courseName}</h2>
            <h2 className="form-title">Бүртгэлийн маягт</h2>
            <p className="form-description">Доорх талбаруудыг бөглөнө үү</p>
          </div>

          {submitSuccess ? (
            <div className="success-message">
              <h2>✓ Амжилттай бүртгэгдлээ!</h2>
              <p>Таны мэдээллийг хүлээн авлаа. Бид тантай удахгүй холбогдох болно.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="registration-form">
              {questions.map((question) => (
                <div key={question.id} className="form-field">
                  <label htmlFor={`question-${question.id}`} className="form-label">
                    {question.question_text}
                    {question.is_required && <span className="required-mark">*</span>}
                  </label>
                  {renderInput(question)}
                  {question.help_text && (
                    <small className="help-text">{question.help_text}</small>
                  )}
                </div>
              ))}

              {submitError && (
                <div className="error-message">
                  {submitError}
                </div>
              )}

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner inline label="Илгээж байна..." /> : 'Илгээх'}
              </button>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default CourseForm;
